import { useMemo } from "react";

/**
 * Detects duplicate emails and phone numbers across all advice rows and their linked ports.
 *
 * A "duplicate email" means the same email string appears on more than one advice row.
 * A "duplicate number" means the same digits-only phone number is linked to more than
 * one advice row (via portsByEmail).
 *
 * @param {Array}  advice       - list of advice-password objects
 * @param {Map}    portsByEmail - email -> port[] map (from usePortsByEmail)
 * @returns {{ duplicateEmails: Set<string>, duplicateNumbers: Set<string> }}
 */
export default function useDuplicateDetection(advice, portsByEmail) {
  return useMemo(() => {
    const emailCount = new Map();   // normalised email -> count
    const numberCount = new Map();  // digits-only number -> count

    (advice || []).forEach((row) => {
      const email = (row.email || "").trim().toLowerCase();
      if (!email) return;

      // Count email occurrences
      emailCount.set(email, (emailCount.get(email) || 0) + 1);

      // Count phone-number occurrences via portsByEmail
      const linkedPorts = portsByEmail.get(email) || [];
      linkedPorts.forEach((port) => {
        const num = String(port.assigned_number || "").replace(/\D/g, "");
        if (!num) return;
        numberCount.set(num, (numberCount.get(num) || 0) + 1);
      });
    });

    const duplicateEmails = new Set(
      [...emailCount.entries()].filter(([, c]) => c > 1).map(([k]) => k)
    );
    const duplicateNumbers = new Set(
      [...numberCount.entries()].filter(([, c]) => c > 1).map(([k]) => k)
    );

    return { duplicateEmails, duplicateNumbers };
  }, [advice, portsByEmail]);
}
